/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useMemo } from 'react';
import { CarouselProvider, ButtonBack, ButtonNext } from 'pure-react-carousel';
import { v4 as uuidv4 } from 'uuid';
import 'pure-react-carousel/dist/react-carousel.es.css';

import { Styled } from './index.styles';
import { ImageCarouselDetailsProps } from '../../organisms/SearchResults/index.types';
import { SaveHotelButton } from '../../molecules/saveHotelButton';
import { renderedData } from '../../organisms/SearchResults/searchResultsMainComponent';
import {
  MARRIOTT_CACHE_URL,
  priorityPhotoCategoryCode,
  CAROUSAL_COMPATABLE_VIEWPORT,
} from '../../organisms/SearchResults/index.constant';
import { useConditionalSearchResultContextSERP } from '../../hooks/lib/useConditionalSearchResultContext';
import { ActiveRibbon } from '../../molecules/ActiveRibbon';
import { PropertyImages } from '../../molecules/PropertyImages';
import { UnprocessedImage } from '../../molecules/PropertyImages/PropertyImages.types';
import { NoImgDiv } from './components/NoImgDiv';
import clsx from 'clsx';
import { APPLICATION_BREAKPOINTS, useCheckBreakpoint, useWindowSize } from '@marriott/mi-ui-library';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { sortImagesUXLResponseWithCategories } from '../../utils/src/ImageCategorySortHelper';
import { CarouselBullets } from '../../molecules/CarouselBullets';
import { useConditionalSearchResultsRenderedDataSERP } from '../../hooks/lib/useConditionalRenderedData';

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface HTMLAttributes<T> {
    // extends React's HTMLAttributes
    custom_click_track_value?: string;
  }
}

// Function to get the file name from the URL to ensure unqiue imgs are displayed in carousel
function getImgFileName(imgURL: string) {
  const fileName = imgURL ? imgURL?.split('?')?.[0]?.split(':')?.[1]?.split('/')?.slice(-1)?.[0]?.split('.')?.[0] : '';
  return fileName;
}

const isValidUrl = (urlString: string, checkedImgIds: string[]): boolean => {
  try {
    const fileName = getImgFileName(urlString);
    const imgPresentAlready = checkedImgIds.includes(fileName);
    if (fileName !== '' && !imgPresentAlready) {
      checkedImgIds.push(fileName);
    }
    const returnVal = Boolean(new URL(urlString)) && !imgPresentAlready;
    return returnVal;
  } catch (e) {
    return false;
  }
};

const getIdWithAffix = (input: string, affix: string): string => {
  let ret = input;
  const hasAffix = String(input).indexOf(affix) > -1;
  if (!hasAffix) {
    ret = `${affix}-${input}`;
  }
  return ret;
};

const normalizeID = (input: string, affix: string): string => {
  return input?.length > 0
    ? `${String(getIdWithAffix(input, String(affix).toLowerCase()))
        .replace(/\s/g, '-')
        .toLowerCase()}`
    : `${affix}-${uuidv4()}`;
};

function getUniqueListBy(arr: any[], key: string) {
  const returnVal = [...new Map(arr?.map(item => [getImgFileName(item[key]), item])).values()];
  return returnVal;
}

export const ImageCarouselComponent: React.FC<ImageCarouselDetailsProps> = ({
  imageList,
  propertyID = '',
  // membersOnlyFlag = false,
  isFullyRenovated = false,
  isRecentlyRenovated = false,
  hasRenovatedRooms = false,
  newLobby = false,
  newProperty = false,
  // idx,
  brandCode = '',
  recentlyViewedFlag,
  brandImageData,
  setHotelSaved,
  hotelName = '',
  seoNickname,
  isMapView,
  disableHeartIcon = false,
  isBrandLogoPresent = false,
  pageVal,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { jsonContent, isMapViewEnabled, sessionObject, configData }: any =
    useConditionalSearchResultsRenderedDataSERP(renderedData);
  const { searchResult } = useConditionalSearchResultContextSERP();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [mediaListArr, setMediaListArr] = useState<any>(imageList);
  const [initialImage, setInitialImage] = useState(imageList);
  const [isHovered, setIsHovered] = useState(false);
  const isViewportL = useCheckBreakpoint('viewportL');
  const { width } = useWindowSize();
  const checkedImgIds: string[] = [];
  const getImageSetup = () => {
    let imageDimensions =
      pageVal === 'chat'
        ? {
            // height: 285,
            // width: 513,
            // height: 288,
            width: 512,
          }
        : {
            height: 288,
            width: 512,
          };
    let selectedImage = jsonContent?.selectedRendition ?? 'wide';
    let fallbackImage = jsonContent?.wideFallBackImageURL;
    if ((width && width <= CAROUSAL_COMPATABLE_VIEWPORT) || isMapViewEnabled) {
      fallbackImage = jsonContent?.classicFallBackImageURL;
      selectedImage = 'classic';
      imageDimensions =
        pageVal === 'chat'
          ? {
              width: 360,
            }
          : {
              height: 240,
              width: 360,
            };
      // imageDimensions = {
      //   height: 214,
      //   width: 320,
      // };
    }
    if (
      isMapView &&
      width &&
      (width > APPLICATION_BREAKPOINTS['viewportL'] || width < APPLICATION_BREAKPOINTS['viewportS'])
    ) {
      selectedImage = 'square';
      imageDimensions =
        pageVal === 'chat'
          ? {
              width: 333,
            }
          : {
              height: 222,
              width: 333,
            };
    }
    if (pageVal === 'chat' && width) {
      if (width > APPLICATION_BREAKPOINTS['viewportXl']) {
        selectedImage = 'classic';
        imageDimensions = {
          width: 540,
        };
      } else if (width > APPLICATION_BREAKPOINTS['viewportL'] && width < APPLICATION_BREAKPOINTS['viewportXl']) {
        selectedImage = 'square';
        imageDimensions = {
          width: 180,
        };
      } else if (width > APPLICATION_BREAKPOINTS['viewportM'] && width < APPLICATION_BREAKPOINTS['viewportL']) {
        selectedImage = 'wide';
        imageDimensions = {
          width: 320,
        };
      } else if (width > APPLICATION_BREAKPOINTS['viewportS'] && width < APPLICATION_BREAKPOINTS['viewportM']) {
        selectedImage = 'square';
        imageDimensions = {
          width: 180,
        };
      } else if (width < APPLICATION_BREAKPOINTS['viewportS']) {
        selectedImage = 'wide';
        imageDimensions = {
          width: 576,
        };
      }

      if (width > APPLICATION_BREAKPOINTS['viewportM'] && isMapViewEnabled) {
        selectedImage = 'wide';
        imageDimensions = {
          width: 576,
        };
      }
    }
    // if (!isViewportM) {
    //   selectedImage = 'classic';
    //   imageDimensions = {
    //     height: 220,
    //     width: 330,
    //   };
    // }
    return { imageDimensions, selectedImage, fallbackImage };
  };

  const { imageDimensions, selectedImage, fallbackImage } = getImageSetup();
  // useEffect(() => {
  //   const imageConfigs = getImageSetup();
  //   imageDimensions = imageConfigs['imageDimensions'];
  //   selectedImage = imageConfigs['selectedImage'];
  //   fallbackImage = imageConfigs['fallbackImage'];
  // }, [isMapViewEnabled]);

  const croppingRatio = 6;
  const outputQuality = 90;
  const outputInterpolation = 'progressive-bilinear';
  /**
   * Construct a string with all arguments for image processing.
   */
  const getImgProcQueryString = useMemo(() => {
    // Ideally we don't need cropping for anything but initial image, 0 index.
    return (
      `?output-quality=${outputQuality}&interpolation=${outputInterpolation}&downsize=` + imageDimensions.width + 'px:*'
    );
  }, [imageDimensions]);

  const getFallbackImgProcQueryString = useMemo(() => {
    // Ideally we don't need cropping for anything but initial image, 0 index.
    return (
      `?output-quality=${outputQuality}&interpolation=${outputInterpolation}&crop=${
        imageDimensions.width * croppingRatio
      }:${imageDimensions.height && imageDimensions.height * croppingRatio};*,*&downsize=` +
      imageDimensions.width +
      'px:*'
    );
  }, [imageDimensions]);

  // **** Image carousel should have limited number of images set from AEM ****
  const maxImagesOnImageCarousel = jsonContent?.maximumImagesOnPropertyCard ?? 10;

  /**
   * Memoized list of images provided from:
   * a) initial image sources
   * b) query results with a body of media objects
   *
   */
  const { envVarsObject } = useClientEnvVarsStore();
  const imageDomain = envVarsObject?.['UXL_IMAGE_CACHE_DOMAIN'] ?? MARRIOTT_CACHE_URL;
  const images = useMemo((): UnprocessedImage[] => {
    /* eslint-disable react-compiler/react-compiler */
    const returnImages: UnprocessedImage[] = [];
    // **** To add Images in the Images URL
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function addImages(imageData: any) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      imageData?.forEach((imagesObj: any) => {
        imagesObj['photosObj']?.edges?.forEach((currentImgObj: any) => {
          const processedObj = currentImgObj?.node;
          const selectedImageByViewPort = Object.keys(processedObj.imageUrls).filter(imageType =>
            imageType.includes(selectedImage)
          );
          const toString = String(processedObj.imageUrls[selectedImageByViewPort[0]]);
          const srcIsValid = toString !== '' && toString.indexOf('null') <= -1 && toString.indexOf('undefined') <= -1;
          const src =
            pageVal === 'chat' && processedObj.imageUrls[selectedImageByViewPort[0]]
              ? `${processedObj.imageUrls[selectedImageByViewPort[0]].indexOf(imageDomain) === -1 ? imageDomain : ''}${
                  processedObj.imageUrls[selectedImageByViewPort[0]]
                }`
              : imageDomain + processedObj.imageUrls[selectedImageByViewPort[0]];
          const searchStr = 'http';
          const hasMultipleHttps = [...src.matchAll(new RegExp(searchStr, 'gi'))].length > 1;
          const imgName = normalizeID(processedObj?.title, propertyID ?? '');
          // Adding checks to see if new images need to be added.
          if (
            processedObj?.title !== returnImages[0]?.name &&
            srcIsValid &&
            !hasMultipleHttps &&
            returnImages.length < maxImagesOnImageCarousel
          ) {
            const isValidSrc = isValidUrl(src, checkedImgIds);
            // Don't add the image if one with matching ID is already in array.
            if (isValidSrc) {
              returnImages.push({
                // Add as env variable
                original: src,
                imageAltText: `${processedObj?.alternateDescription}`,
                name: imgName,
              });
            }
          }
        });
      });
    }
    // **** Setting Images for rendering the carousel ****
    // "mediaListArr" is an array. While, the "photos" endpoint is an array, the "photoGallery" endpoint
    // is not. Therefore, whenever, we set the "mediaListArr" value, we need to set it to a array value
    // so that the treatment is the same in both cases, irrespective of the source endpoint.
    const selectedImageByViewPortName =
      mediaListArr && mediaListArr?.length > 0 && mediaListArr[0].node && mediaListArr[0].node?.imageUrls
        ? Object.keys(mediaListArr[0].node?.imageUrls).filter(imageType => imageType.includes(selectedImage))
        : [];
    if (
      mediaListArr?.length &&
      (mediaListArr[0].node ? mediaListArr[0].node?.imageUrls[selectedImageByViewPortName[0]] : true)
    ) {
      // eslint-disable-next-line no-prototype-builtins

      if (Object.prototype.hasOwnProperty.call(mediaListArr[0], 'content')) {
        returnImages.push({
          original: mediaListArr[0]?.content[0]?.url,
          imageAltText: mediaListArr[0]?.content[0]?.alternateText ?? mediaListArr[0]?.content[0]?.name,
          name: normalizeID(mediaListArr[0]?.content[0]?.name, propertyID ?? ''),
        });
      } else if (Object.prototype.hasOwnProperty.call(mediaListArr[0], 'node')) {
        const selectedImageByViewPort = Object.keys(mediaListArr[0].node?.imageUrls).filter(imageType =>
          imageType.includes(selectedImage)
        );
        const src =
          pageVal === 'shop'
            ? imageDomain + mediaListArr[0].node?.imageUrls[selectedImageByViewPort[0]]
            : `${
                mediaListArr[0].node?.imageUrls[selectedImageByViewPort[0]].indexOf(imageDomain) === -1
                  ? imageDomain
                  : ''
              }${mediaListArr[0].node?.imageUrls[selectedImageByViewPort[0]]}`;
        returnImages.push({
          original: src,
          imageAltText: mediaListArr[0]?.node?.alternateDescription,
          name: normalizeID(mediaListArr[0]?.node?.title, propertyID ?? ''),
        });
      } else {
        if (initialImage && initialImage.length) {
          if (Object.prototype.hasOwnProperty.call(initialImage[0], 'content')) {
            returnImages.push({
              original: initialImage[0]?.content[0]?.url,
              imageAltText: initialImage[0]?.content[0]?.alternateText,
              name: normalizeID(initialImage[0]?.content[0]?.name, propertyID ?? ''),
            });
          } else if (Object.prototype.hasOwnProperty.call(initialImage[0], 'node')) {
            const selectedImageByViewPort = Object.keys(initialImage[0]?.node?.imageUrls).filter(imageType =>
              imageType.includes(selectedImage)
            );
            const src = imageDomain + initialImage[0]?.node?.imageUrls[selectedImageByViewPort[0]];
            returnImages.push({
              original: src,
              imageAltText: initialImage[0]?.node?.alternateDescription,
              name: normalizeID(initialImage[0]?.node?.title, propertyID ?? ''),
            });
          }
        }

        // Add preferred category images first into the Image carousel
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const categoryImages = mediaListArr?.filter((v: any) => v?.photoCategory?.code === priorityPhotoCategoryCode);
        // const categoryImagesFiltered = getUniqueListBy(categoryImages, 'name');
        const nonCategoryImages = mediaListArr?.filter(
          (v: { photoCategory: { code: string } }) => v?.photoCategory?.code !== priorityPhotoCategoryCode
        );
        const sortedArray = sortImagesUXLResponseWithCategories(nonCategoryImages);
        addImages([...categoryImages, ...sortedArray]);
      }
    } else {
      if (
        initialImage &&
        initialImage[0] &&
        (initialImage[0].node ? initialImage[0].node?.imageUrls[selectedImageByViewPortName[0]] : true)
      ) {
        returnImages.push({
          original: initialImage[0]?.node?.imageUrls[selectedImageByViewPortName[0]],
          imageAltText: initialImage[0]?.node?.alternateDescription,
          name: normalizeID(initialImage[0]?.node?.title, propertyID ?? ''),
        });
      } else {
        setInitialImage([
          {
            content: [
              {
                url: brandImageData && brandImageData.length ? brandImageData[0]?.url : '',
                alternateText:
                  brandImageData && brandImageData.length
                    ? brandImageData[0]?.alternateText ?? brandImageData[0]?.name
                    : '',
                name:
                  brandImageData && brandImageData.length ? normalizeID(brandImageData[0]?.name, propertyID ?? '') : '',
              },
            ],
          },
        ]);
        // **** If Initial image and new Images are not found for the Property ****
        // **** then we need to use Brand Image in the carousel ****
        returnImages.push({
          original: brandImageData && brandImageData?.length ? brandImageData[0]?.url : '',
          imageAltText:
            brandImageData && brandImageData?.length ? brandImageData[0]?.alternateText ?? brandImageData[0]?.name : '',
          name: brandImageData && brandImageData?.length ? normalizeID(brandImageData[0]?.name, propertyID ?? '') : '',
        });
      }
    }
    // **** Setting Images for rendering the carousel ****
    // If only one, return that one.
    if (returnImages.length <= 1) return returnImages;
    // If more than one, remove duplicate IDs and then slice to max length before returning.
    const deDuped = getUniqueListBy(returnImages, 'original') as UnprocessedImage[];
    return deDuped.length > maxImagesOnImageCarousel ? deDuped.slice(0, 9) : deDuped;
    /* eslint-enable react-compiler/react-compiler */
  }, [mediaListArr, maxImagesOnImageCarousel, imageDimensions, selectedImage, imageDomain]);

  const clickTrackingLoc = 'Phoenix Search Results';

  // **** Updating the MediaList Array when we receive the media tour data from UXL ****
  useEffect(() => {
    if (searchResult.media) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const propertyMedia = searchResult.media.filter((key: any) => key.id === propertyID);
      if (propertyMedia.length) {
        const photoGalleryObj = propertyMedia[0]?.media.photoGallery;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const propertyImageList: any = [];
        Object.keys(photoGalleryObj).forEach((categoryKey: any) => {
          const propertyObject: any = {};
          if (categoryKey !== '__typename' && photoGalleryObj[categoryKey]) {
            propertyObject['categoryCode'] = categoryKey;
            propertyObject['photosObj'] = photoGalleryObj[categoryKey];
            propertyImageList.push(propertyObject);
          }
        });
        if (propertyImageList.length > 0) {
          setMediaListArr(propertyImageList);
        }
      }
    }
  }, [searchResult.media]);
  // **** Updating the MediaList Array when we receive the media tour data from UXL ****

  // **** Fallback Images dimensions change based on screen resolution ****
  const fallbackImageSrc = fallbackImage + getFallbackImgProcQueryString;
  const brandImageSrcUrl = brandImageData?.length ? brandImageData[0].url + getFallbackImgProcQueryString : '';
  // **** Fallback Images dimensions change based on screen resolution end ****

  const [slideCount, setSlideCount] = useState<number>(10);

  return (
    <Styled pageVal={pageVal}>
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={'image-gallery-component ' + (isMapViewEnabled ? `map-view-enabled` : ``)}
      >
        <div className="glide-wrap">
          {images?.length > 0
            ? pageVal !== 'chat' && (
                <div className="additional-components">
                  {sessionObject?.consumerID && sessionObject?.accessToken && !disableHeartIcon && (
                    <SaveHotelButton propertyID={propertyID} setHotelSaved={setHotelSaved} />
                  )}
                  <div className="ribbons-container">
                    <ActiveRibbon
                      {...{
                        jsonContent,
                        recentlyViewedFlag,
                        isFullyRenovated,
                        isRecentlyRenovated,
                        hasRenovatedRooms,
                        newProperty,
                        newLobby,
                      }}
                    />
                  </div>
                </div>
              )
            : null}
          {images?.length ? (
            <CarouselProvider
              naturalSlideWidth={imageDimensions.width}
              naturalSlideHeight={imageDimensions.height || 288}
              totalSlides={slideCount}
              infinite={true}
              dragEnabled={false}
              isIntrinsicHeight={true}
              aria-live="off"
            >
              <PropertyImages
                {...{
                  images,
                  getImgProcQueryString,
                  imageDimensions,
                  brandImageSrcUrl,
                  fallbackImageSrc,
                  propertyID,
                  setSlideCount,
                  hotelName,
                  selectedImage,
                  isBrandLogoPresent,
                }}
              />
              {slideCount > 1 &&
              ((pageVal === 'chat' && isHovered) || (pageVal === 'chat' && !isViewportL) || pageVal !== 'chat') ? (
                <>
                  <ButtonBack
                    className={clsx('custom_click_track m-button-secondary-inverse')}
                    {...{ custom_click_track_value: `${clickTrackingLoc}| Image Carousel Arrow |internal` }}
                  >
                    <span className="icon-arrow-left" aria-hidden="true" />
                  </ButtonBack>

                  <ButtonNext
                    className={clsx('custom_click_track m-button-secondary-inverse')}
                    {...{ custom_click_track_value: `${clickTrackingLoc}| Image Carousel Arrow |internal` }}
                  >
                    <span className="icon-arrow-right" aria-hidden="true" />
                  </ButtonNext>
                </>
              ) : null}
              {slideCount > 1 ? <CarouselBullets slideCount={slideCount} aria-hidden="true" /> : null}
            </CarouselProvider>
          ) : (
            <NoImgDiv
              {...{
                configData,
                propertyID,
                brandCode,
                clickTrackingLoc,
                imageDimensions,
                fallbackImageSrc,
                fallbackImagesAlt: jsonContent?.fallBackImageAltText,
                hotelName,
                seoNickname,
              }}
            />
          )}
        </div>
      </div>
    </Styled>
  );
};
